import { useState } from "react";
import { Palette, Shuffle, Sparkles, RotateCcw, Loader2, Check, Bookmark, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu";
import { HOLIDAYS, activeTheme, applyPalette, holidayFor, resetTheme, setAuto, setHoliday, setPreview, shuffleArtTheme, useTheme } from "@/lib/theme";
import { deletePalette, findSaved, savePalette, usePalettes } from "@/lib/palettes";
import { PaletteRenameDialog } from "@/components/PalettesSection";
import { formatApiError } from "@/lib/api";

const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
const hoverPreview = (colors) => ({ onPointerEnter: () => setPreview(colors), onFocus: () => setPreview(colors), onPointerLeave: () => setPreview(null), onBlur: () => setPreview(null) });

const Dots = ({ colors }) => (
  <span className="ml-auto flex gap-0.5 pl-3">{colors.slice(0, 4).map((c, i) => <span key={i} className="h-2.5 w-2.5 rounded-full ring-1 ring-black/10" style={{ background: c }} />)}</span>
);

/* Nav-bar "Themes" menu: shuffle a palette from random cover art, save/reapply favourites, pick a holiday, or let the season decide. */
export const ThemeButton = () => {
  const s = useTheme();
  const theme = activeTheme(s);
  const saved = usePalettes();
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(null);
  if (!s.enabled) return null;
  const season = holidayFor();
  const current = theme?.kind === "art" ? findSaved(theme.colors) : null;
  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); if (okMsg && r !== false) toast.success(okMsg); return r; }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const shuffle = () => run(async () => {
    const ok = await shuffleArtTheme();
    if (!ok) toast.message("No cover art yet", { description: "Add albums with artwork to build a theme from them." });
    return ok;
  });
  const save = () => run(() => savePalette(theme.colors), "Palette saved to your account");
  const remove = (p) => run(() => deletePalette(p.id), `Deleted “${p.name}”`);
  const iconBtn = "flex h-6 w-6 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-200 hover:text-zinc-900";
  return (
    <>
      <DropdownMenu onOpenChange={(o) => !o && setPreview(null)}>
        <DropdownMenuTrigger asChild>
          <button type="button" data-testid="nav-theme-button" title="Background themes" aria-label="Background themes"
            className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-700 transition-colors hover:border-zinc-900 hover:text-zinc-900">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Palette size={14} />}
            {theme && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ background: theme.colors[0] }} data-testid="nav-theme-dot" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72" data-testid="theme-menu">
          <DropdownMenuItem onSelect={shuffle} disabled={busy} data-testid="theme-shuffle-art" className="gap-2">
            <Shuffle size={14} />Shuffle from cover art
            {theme?.kind === "art" && !current && <Check size={14} className="ml-auto text-amber-600" />}
          </DropdownMenuItem>
          {theme?.kind === "art" && !current && (
            <DropdownMenuItem onSelect={save} disabled={busy} data-testid="theme-save-palette" className="gap-2 text-amber-700 focus:text-amber-800">
              <Bookmark size={14} />Save this palette<Dots colors={theme.colors} />
            </DropdownMenuItem>
          )}
          {saved.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Saved</DropdownMenuLabel>
              {saved.map((p) => (
                <DropdownMenuItem key={p.id} onSelect={() => { setPreview(null); applyPalette(p); }} data-testid={`theme-saved-${p.id}`} className="group gap-2" {...hoverPreview(p.colors)}>
                  {current?.id === p.id ? <Check size={14} className="text-amber-600" /> : <span className="w-3.5" />}
                  <span className="truncate" data-testid={`theme-saved-name-${p.id}`}>{p.name}</span>
                  <Dots colors={p.colors} />
                  <span className="ml-1 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100" onPointerDown={stop} onPointerUp={stop} onClick={stop}>
                    <button type="button" onClick={(e) => { stop(e); setRenaming(p); }} className={iconBtn} data-testid={`theme-saved-rename-${p.id}`} title="Rename" aria-label={`Rename ${p.name}`}><Pencil size={11} /></button>
                    <button type="button" onClick={(e) => { stop(e); remove(p); }} className={iconBtn} data-testid={`theme-saved-delete-${p.id}`} title="Delete" aria-label={`Delete ${p.name}`}><X size={11} /></button>
                  </span>
                </DropdownMenuItem>
              ))}
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Holidays</DropdownMenuLabel>
          {HOLIDAYS.map((h) => (
            <DropdownMenuItem key={h.id} onSelect={() => { setPreview(null); setHoliday(h.id); }} data-testid={`theme-holiday-${h.id}`} className="gap-2" {...hoverPreview(h.colors)}>
              {theme?.kind === "holiday" && theme.id === h.id ? <Check size={14} className="text-amber-600" /> : <span className="w-3.5" />}
              {h.label}{season?.id === h.id && <span className="rounded bg-amber-50 px-1 text-[9px] uppercase text-amber-700">Now</span>}
              <Dots colors={h.colors} />
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem checked={s.auto && s.kind === "none"} onCheckedChange={setAuto} data-testid="theme-auto-season" className="gap-2">
            <Sparkles size={14} />Auto by season
          </DropdownMenuCheckboxItem>
          <DropdownMenuItem onSelect={() => { setPreview(null); resetTheme(); }} disabled={!theme && !s.auto} data-testid="theme-reset" className="gap-2 text-zinc-500" {...hoverPreview([])}>
            <RotateCcw size={14} />Plain background
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {renaming && <PaletteRenameDialog palette={renaming} onClose={() => setRenaming(null)} />}
    </>
  );
};
